import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./SacredCircuitsBook.css";

const circuits = [
  {
    id: "pancha-bhoota",
    badge: "Five Elemental Shivalayas",
    chant: "ॐ नमः शिवाय",
    chantMeaning: "Kritam Sarvam Shiva-Mayam",
    title: "Pancha Bhoota Sthalam",
    subtitle: "The Five Elemental Manifestations of Lord Shiva",
    description: "The universe and the human body are composed of the five sacred elements (Pancha Bhootas). In South India, Lord Shiva manifests Himself as the primordial essence of Earth, Water, Fire, Air, and Space across five timeless temples, guiding the soul from mortal elements to transcendent liberation.",
    attributes: [
      { label: "Earth (Prithvi)", val: "Kanchipuram" },
      { label: "Water (Appu)", val: "Thiruvanaikaval" },
      { label: "Fire (Theyu)", val: "Tiruvannamalai" },
      { label: "Air (Vayu)", val: "Srikalahasti" },
      { label: "Space (Akasha)", val: "Chidambaram" },
    ],
    link: "/pancha-bhoota-sthalam",
    actionText: "Explore Elemental Shrines",
  },
  {
    id: "arupadai-veedu",
    badge: "Six Sacred Abodes of Murugan",
    chant: "ॐ शरवणभव",
    chantMeaning: "Skandaya Kartikeyaya Namaha",
    title: "Arupadai Veedu",
    subtitle: "The Six Holy Encampments of Lord Murugan",
    description: "The Arupadai Veedu represents the six sacred military and spiritual abodes where Lord Murugan conquered negative cosmic forces and bestowed eternal grace. Spanning holy hills and sacred seashores across Tamil Nadu, this circuit awakens courage, spiritual victory, and supreme divine wisdom.",
    attributes: [
      { label: "Holy Battlefield", val: "Tiruchendur" },
      { label: "Sacred Hills", val: "Palani & Swamimalai" },
      { label: "Divine Abodes", val: "Thiruparankundram" },
      { label: "Peace & Grace", val: "Thiruthani & Pazhamudircholai" },
    ],
    link: "/arupadai-veedu",
    actionText: "Discover Sacred Abodes",
  },
  {
    id: "mukti-kshetras",
    badge: "Shrines of Liberation",
    chant: "ॐ नमो नारायणाय",
    chantMeaning: "Moksha-Dayaka Sapta Kshetrani",
    title: "Mukti Kshetras of Karnataka",
    subtitle: "Seven Ancient Portals for Spiritual Emancipation",
    description: "Established by Sage Parashurama along the sacred Western Ghats and coastal Karnataka, these seven pilgrimage centers hold the divine power to cleanse lifelong karmas and grant Moksha (ultimate liberation), uniting the divine grace of Lord Shiva, Vishnu, and the Supreme Mother.",
    attributes: [
      { label: "Atmalinga", val: "Gokarna" },
      { label: "Krishna Kshetra", val: "Udupi" },
      { label: "Maha Shakti", val: "Kollur Mookambika" },
      { label: "Sarpa Dosha Nivarana", val: "Kukke Subramanya" },
    ],
    link: "/mukti-kshetras-karnataka",
    actionText: "Explore Liberation Shrines",
  },
  {
    id: "shakti-peethas",
    badge: "51 Divine Peethas",
    chant: "ॐ ऐं ह्रीं क्लीं चामुण्डायै विच्चे",
    chantMeaning: "Sarva-Shakti Swaroopini Mata",
    title: "Sacred Seats of Shakti",
    subtitle: "Primordial Cosmic Energy Across the Sacred Subcontinent",
    description: "When Lord Shiva performed the cosmic dance carrying Sati's sacred body, Sudarshana Chakra dispersed Her forms across 51 holy shrines. Each Peetha radiates supreme cosmic power (Adi Parashakti), awakening spiritual consciousness, supreme protection, and boundless maternal grace.",
    attributes: [
      { label: "Sacred Portals", val: "51 Consecrated Shakti Peethas" },
      { label: "Divine Guardians", val: "51 Bhairava Shrines" },
      { label: "Cosmic Energy", val: "Primordial Kundalini Power" },
    ],
    link: "/shakti-peethas",
    actionText: "Start Your Journey",
  },
  {
    id: "char-dham",
    badge: "Himalayan Pilgrimage",
    chant: "ॐ नमो भगवते वासुदेवाय",
    chantMeaning: "Himalaya-Vasi Moksha Dhamam",
    title: "Char Dham Yatra",
    subtitle: "The Four Sacred Pillars of Eternal Liberation",
    description: "Perched high within the snow-clad peaks of the Garhwal Himalayas, the Char Dham represents the ultimate spiritual odyssey in Sanatana Dharma. Consecrated by Adi Shankaracharya, a pilgrimage to Yamunotri, Gangotri, Kedarnath, and Badrinath releases the seeker from the cycle of rebirth.",
    attributes: [
      { label: "Holy River Sources", val: "Yamunotri & Gangotri" },
      { label: "Jyotirlinga Peak", val: "Kedarnath (Lord Shiva)" },
      { label: "Vaikuntha on Earth", val: "Badrinath (Lord Vishnu)" },
    ],
    link: "/char-dham-yatra",
    actionText: "Begin The Sacred Journey",
  },
  {
    id: "amarnath-yatra",
    badge: "Sacred Cave of Kashmir",
    chant: "ॐ त्र्यम्बकं यजामहे",
    chantMeaning: "Amreshwaraya Mahadevaya Namaha",
    title: "Amarnath Yatra",
    subtitle: "The Cave of Immortality & Self-Manifested Ice Lingam",
    description: "Nestled at an altitude of 3,888 meters amid the majestic snow mountains of Kashmir, the holy Amarnath Cave is where Lord Shiva revealed the mystery of creation and immortality (Amar Katha) to Goddess Parvati. Pilgrims witness the miraculous self-forming Ice Shiva Lingam.",
    attributes: [
      { label: "Altitude", val: "3,888m High Himalayan Cave" },
      { label: "Divine Miracle", val: "Natural Ice Shiva Lingam" },
      { label: "Eternal Legend", val: "Amar Katha & Immortal Doves" },
    ],
    link: "/amarnath-yatra",
    actionText: "Begin Holy Pilgrimage",
  },
];

const TOTAL_SPREADS = 4; // 0 = Cover, 1 = Spread 1, 2 = Spread 2, 3 = Spread 3

const SacredCircuitsBook = () => {
  const [spreadIndex, setSpreadIndex] = useState(0);
  const touchStartX = useRef(null);

  const goToNextSpread = useCallback(() => {
    if (spreadIndex < TOTAL_SPREADS - 1) {
      setSpreadIndex((prev) => prev + 1);
    }
  }, [spreadIndex]);

  const goToPrevSpread = useCallback(() => {
    if (spreadIndex > 0) {
      setSpreadIndex((prev) => prev - 1);
    }
  }, [spreadIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") goToNextSpread();
      else if (e.key === "ArrowLeft") goToPrevSpread();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNextSpread, goToPrevSpread]);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX.current - touchEndX;
    if (diffX > 40) goToNextSpread();
    else if (diffX < -40) goToPrevSpread();
    touchStartX.current = null;
  };

  return (
    <div className="w-full flex flex-col items-center select-none">
      {/* 3D Book Viewport Stage */}
      <div 
        className="book-stage relative w-full max-w-6xl flex items-center justify-center py-4 px-2 sm:px-4 min-h-[540px] md:min-h-[620px] lg:min-h-[660px]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Previous Button (<) */}
        <button
          onClick={goToPrevSpread}
          disabled={spreadIndex === 0}
          className={`absolute left-0 sm:left-2 md:-left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white text-slate-800 shadow-2xl border border-slate-200 hover:border-sky-400 flex items-center justify-center transition-all duration-200 ${
            spreadIndex === 0
              ? "opacity-0 scale-75 pointer-events-none"
              : "opacity-95 hover:opacity-100 hover:scale-110 active:scale-95 hover:bg-sky-50 hover:text-sky-600"
          }`}
          aria-label="Previous Page"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Next Button (>) */}
        <button
          onClick={goToNextSpread}
          disabled={spreadIndex === TOTAL_SPREADS - 1}
          className={`absolute right-0 sm:right-2 md:-right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white text-slate-800 shadow-2xl border border-slate-200 hover:border-sky-400 flex items-center justify-center transition-all duration-200 ${
            spreadIndex === TOTAL_SPREADS - 1
              ? "opacity-0 scale-75 pointer-events-none"
              : "opacity-95 hover:opacity-100 hover:scale-110 active:scale-95 hover:bg-sky-50 hover:text-sky-600"
          }`}
          aria-label="Next Page"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* 3D Book Container */}
        <div 
          className={`book-container relative transition-all duration-300 ease-out ${
            spreadIndex === 0 
              ? "w-[320px] sm:w-[400px] md:w-[480px] lg:w-[520px] h-[520px] md:h-[580px] lg:h-[620px]" 
              : "w-[340px] sm:w-[680px] md:w-[940px] lg:w-[1080px] xl:w-[1140px] h-[540px] sm:h-[560px] md:h-[600px] lg:h-[640px]"
          }`}
        >
          {/* SPREAD 0: MODERN IVORY PEARL & ROYAL SLATE COVER */}
          {spreadIndex === 0 && (
            <div 
              onClick={goToNextSpread}
              className="w-full h-full rounded-2xl md:rounded-3xl cursor-pointer overflow-hidden book-cover-ivory transition-all duration-300 hover:scale-[1.015] relative flex flex-col justify-between p-7 sm:p-9 md:p-11 text-slate-900 book-stack-right group"
            >
              {/* Subtle Gold Dust Aura */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(197,160,89,0.12),transparent_70%)] pointer-events-none"></div>
              
              {/* Double Champagne-Gold Ornate Frame */}
              <div className="absolute inset-3.5 md:inset-5 rounded-xl md:rounded-2xl ivory-cover-frame pointer-events-none"></div>
              <div className="absolute inset-5 md:inset-7 rounded-lg md:rounded-xl ivory-cover-inner-frame pointer-events-none"></div>

              {/* Corner Accents */}
              <div className="absolute top-5 left-5 w-5 h-5 border-t-2 border-l-2 border-[#c5a059] pointer-events-none"></div>
              <div className="absolute top-5 right-5 w-5 h-5 border-t-2 border-r-2 border-[#c5a059] pointer-events-none"></div>
              <div className="absolute bottom-5 left-5 w-5 h-5 border-b-2 border-l-2 border-[#c5a059] pointer-events-none"></div>
              <div className="absolute bottom-5 right-5 w-5 h-5 border-b-2 border-r-2 border-[#c5a059] pointer-events-none"></div>

              {/* Top Header Badge */}
              <div className="relative z-10 text-center pt-2">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 border border-[#c5a059] text-xs md:text-sm font-bold uppercase tracking-widest shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                  Sacred Anthology
                </div>
                <div className="text-[#b8860b] text-4xl font-serif mt-3 filter drop-shadow-sm">
                  ॐ
                </div>
              </div>

              {/* Cover Main Title */}
              <div className="relative z-10 text-center px-4">
                <h3 className="text-2xl sm:text-3xl md:text-5xl font-serif font-black tracking-tight text-slate-900 leading-snug">
                  Sacred Pilgrimages & Holy Circuits
                </h3>
                <div className="w-24 h-0.5 mx-auto bg-[#c5a059] my-4"></div>
                <p className="text-slate-600 text-sm sm:text-base md:text-lg font-medium max-w-sm mx-auto leading-relaxed">
                  A Divine Guide to India's Eternal Sanctuaries, Elemental Shrines, and Abodes of Liberation.
                </p>
              </div>

              {/* Cover Action Button */}
              <div className="relative z-10 text-center pb-2">
                <div className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm md:text-base font-bold shadow-xl shadow-slate-900/20 group-hover:scale-105 active:scale-95 transition-all border border-[#c5a059]">
                  <span className="text-amber-300">Open Sacred Book</span>
                  <svg className="w-5 h-5 text-amber-300 translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* SPREADS 1, 2, 3: OPEN SPREAD (Manuscript Format Without Images) */}
          {spreadIndex > 0 && (
            <div className="w-full h-full p-2.5 sm:p-3 md:p-4 rounded-2xl md:rounded-3xl book-outer-casing shadow-2xl relative flex flex-col justify-center">
              {/* Inner Parchment/White Pages Container */}
              <div className="w-full h-full flex flex-col sm:flex-row rounded-xl md:rounded-2xl overflow-hidden bg-white relative shadow-inner border border-slate-200">
                {/* Visible Central Spine Partition */}
                <div className="hidden sm:block book-spine-partition"></div>

                {/* LEFT PAGE */}
                {(() => {
                  const leftCircuitIndex = (spreadIndex - 1) * 2;
                  const item = circuits[leftCircuitIndex];
                  if (!item) return null;

                  return (
                    <div className="w-full sm:w-1/2 h-full book-page-left p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-between relative border-b sm:border-b-0 sm:border-r border-slate-200 group">
                      {/* Top Header Row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-xs font-bold uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                          {item.badge}
                        </div>
                        <span className="text-slate-400 font-serif font-bold text-xs bg-slate-100 px-2.5 py-1 rounded border border-slate-200">
                          Page {leftCircuitIndex + 1}
                        </span>
                      </div>

                      {/* Content: Heading & Small Introduction */}
                      <div className="my-auto flex flex-col gap-3 sm:gap-4 py-3">
                        <div>
                          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-black text-slate-900 group-hover:text-sky-600 transition-colors leading-tight">
                            {item.title}
                          </h3>
                          {item.subtitle && (
                            <p className="text-xs sm:text-sm md:text-base font-semibold text-sky-700 mt-1.5">
                              {item.subtitle}
                            </p>
                          )}
                        </div>

                        <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      {/* Button for going to the page */}
                      <div className="pt-2">
                        <Link
                          to={item.link}
                          className="inline-flex items-center justify-between sm:justify-center gap-3 px-6 py-3 rounded-xl bg-slate-900 hover:bg-sky-600 text-white font-bold text-sm sm:text-base shadow-lg hover:shadow-sky-500/25 transition-all duration-300 active:scale-95 group/btn w-full sm:w-auto"
                        >
                          <span>{item.actionText}</span>
                          <div className="w-7 h-7 rounded-full bg-white/10 group-hover/btn:bg-white/20 flex items-center justify-center transition-all">
                            <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        </Link>
                      </div>
                    </div>
                  );
                })()}

                {/* RIGHT PAGE */}
                {(() => {
                  const rightCircuitIndex = (spreadIndex - 1) * 2 + 1;
                  const item = circuits[rightCircuitIndex];
                  if (!item) return null;

                  return (
                    <div className="w-full sm:w-1/2 h-full book-page-right p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-between relative group">
                      {/* Top Header Row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-xs font-bold uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                          {item.badge}
                        </div>
                        <span className="text-slate-400 font-serif font-bold text-xs bg-slate-100 px-2.5 py-1 rounded border border-slate-200">
                          Page {rightCircuitIndex + 1}
                        </span>
                      </div>

                      {/* Content: Heading & Small Introduction */}
                      <div className="my-auto flex flex-col gap-3 sm:gap-4 py-3">
                        <div>
                          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-black text-slate-900 group-hover:text-sky-600 transition-colors leading-tight">
                            {item.title}
                          </h3>
                          {item.subtitle && (
                            <p className="text-xs sm:text-sm md:text-base font-semibold text-sky-700 mt-1.5">
                              {item.subtitle}
                            </p>
                          )}
                        </div>

                        <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      {/* Button for going to the page */}
                      <div className="pt-2">
                        <Link
                          to={item.link}
                          className="inline-flex items-center justify-between sm:justify-center gap-3 px-6 py-3 rounded-xl bg-slate-900 hover:bg-sky-600 text-white font-bold text-sm sm:text-base shadow-lg hover:shadow-sky-500/25 transition-all duration-300 active:scale-95 group/btn w-full sm:w-auto"
                        >
                          <span>{item.actionText}</span>
                          <div className="w-7 h-7 rounded-full bg-white/10 group-hover/btn:bg-white/20 flex items-center justify-center transition-all">
                            <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        </Link>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SacredCircuitsBook;
